import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import { useI18n } from "@/i18n";
import { getAttribution } from "@/lib/attribution";
import { trackEvent, trackFbq } from "@/lib/analytics";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr as frLocale, enGB as enLocale } from "date-fns/locale";

const COPY = {
  fr: {
    name: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    type: "Type d'événement",
    date: "Date souhaitée",
    guests: "Nombre d'invités",
    origin: "Comment nous avez-vous connu ?",
    message: "Message",
    submit: "Envoyer ma demande",
    sending: "Envoi…",
    types: [
      "Mariage",
      "Séminaire / entreprise",
      "Baptême / communion",
      "Anniversaire / fête de famille",
      "Cocktail",
      "Autre",
    ],
    origins: [
      "Google",
      "Instagram / Facebook",
      "Mariages.net ou autre annuaire",
      "Wedding planner ou prestataire",
      "Mairie",
      "Bouche-à-oreille",
      "Autre",
    ],
  },
  en: {
    name: "Name",
    email: "Email",
    phone: "Phone",
    type: "Type of event",
    date: "Preferred date",
    guests: "Number of guests",
    origin: "How did you hear about us?",
    message: "Message",
    submit: "Send my enquiry",
    sending: "Sending…",
    types: [
      "Wedding",
      "Seminar / corporate",
      "Christening / communion",
      "Birthday / family celebration",
      "Cocktail",
      "Other",
    ],
    origins: [
      "Google",
      "Instagram / Facebook",
      "Mariages.net or another directory",
      "Wedding planner or supplier",
      "Town hall",
      "Word of mouth",
      "Other",
    ],
  },
};

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseISODate = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

/**
 * Shared quote (devis) form — used on /evenements and on every event
 * landing page. Sends `page_source` so we know which page generated
 * the lead, and fires the Meta Pixel Lead event on success.
 */
export default function DevisForm({ defaultEventType, pageSource }) {
  const { lang, localePath } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const c = COPY[lang === "en" ? "en" : "fr"];
  const source = pageSource || location.pathname;

  const [form, setForm] = useState({
    event_type: defaultEventType || c.types[0],
    event_date: "",
    guests: "",
    name: "",
    email: "",
    phone: "",
    origin: "",
    message: "",
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [status, setStatus] = useState({ loading: false, error: "" });

  const dfLocale = lang === "en" ? enLocale : frLocale;
  const selectedDate = parseISODate(form.event_date);
  const dateLabel = selectedDate
    ? format(selectedDate, "EEEE d MMMM yyyy", { locale: dfLocale })
    : c.date;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "" });
    try {
      await api.post("/events/requests", {
        ...form,
        guests: Number(form.guests) || 0,
        page_source: source,
        attribution: getAttribution() || {},
      });
      // Lead conversion — fired here so we know which page generated it.
      trackFbq("Lead", { content_category: form.event_type, content_name: source });
      trackEvent("generate_lead", {
        form_name: "events",
        content_category: form.event_type,
        content_name: source,
        guests: Number(form.guests) || 0,
      });
      const q = new URLSearchParams({
        form: "events",
        type: form.event_type || "",
        guests: String(Number(form.guests) || 0),
        lead: "1",
      }).toString();
      navigate(`${localePath("/merci")}?${q}`);
    } catch (err) {
      setStatus({
        loading: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="devis-form">
      <input type="hidden" name="page_source" value={source} data-testid="devis-page-source" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <select
          data-testid="devis-input-type"
          value={form.event_type}
          onChange={onChange("event_type")}
          aria-label={c.type}
          className="field"
        >
          {c.types.map((tt) => (
            <option key={tt} value={tt}>{tt}</option>
          ))}
        </select>

        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="devis-input-date"
              aria-label={c.date}
              className={`field text-left flex items-center justify-between gap-2 ${selectedDate ? "text-[#203240]" : "text-[#697585]"}`}
            >
              <span className="truncate capitalize">{dateLabel}</span>
              <CalendarIcon size={16} className="text-[#B8956A] shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 bg-[#FFFFFF] border-[#D9D3C5]">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (d) {
                  setForm((f) => ({ ...f, event_date: toISODate(d) }));
                  setDateOpen(false);
                }
              }}
              locale={dfLocale}
              weekStartsOn={1}
              disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <input
        data-testid="devis-input-guests"
        type="number"
        min={1}
        value={form.guests}
        onChange={onChange("guests")}
        placeholder={c.guests}
        aria-label={c.guests}
        className="field"
      />
      <input
        data-testid="devis-input-name"
        required
        value={form.name}
        onChange={onChange("name")}
        placeholder={c.name}
        aria-label={c.name}
        className="field"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input
          data-testid="devis-input-email"
          required
          type="email"
          value={form.email}
          onChange={onChange("email")}
          placeholder={c.email}
          aria-label={c.email}
          className="field"
        />
        <input
          data-testid="devis-input-phone"
          value={form.phone}
          onChange={onChange("phone")}
          placeholder={c.phone}
          aria-label={c.phone}
          className="field"
        />
      </div>
      <select
        data-testid="devis-input-origin"
        value={form.origin}
        onChange={onChange("origin")}
        aria-label={c.origin}
        className={`field ${form.origin ? "text-[#203240]" : "text-[#697585]"}`}
      >
        <option value="">{c.origin}</option>
        {c.origins.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <textarea
        data-testid="devis-input-message"
        value={form.message}
        onChange={onChange("message")}
        placeholder={c.message}
        aria-label={c.message}
        rows={4}
        className="field"
      />

      {status.error && (
        <p data-testid="devis-error" className="text-sm text-red-500">{status.error}</p>
      )}

      <button
        data-testid="devis-submit"
        type="submit"
        disabled={status.loading}
        className="btn-primary w-full justify-center"
      >
        {status.loading ? c.sending : c.submit} <ArrowRight size={14} />
      </button>
    </form>
  );
}
